// Google Analytics 4 configuration
export const GA4_MEASUREMENT_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID || 'G-XXXXXXXXXX';

// Declare gtag function
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

// Initialize GA4
export const initGA4 = () => {
  if (typeof window === 'undefined' || !GA4_MEASUREMENT_ID) return;

  // Load gtag script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer.push(arguments);
  };

  // Configure GA4
  window.gtag('js', new Date());
  window.gtag('config', GA4_MEASUREMENT_ID, {
    send_page_view: false, // We'll handle page views manually
    cookie_flags: 'SameSite=Strict;Secure',
  });

  console.log('📊 GA4 initialized with ID:', GA4_MEASUREMENT_ID);
};

// Track page views
export const trackPageView = (url: string, title?: string) => {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', 'page_view', {
    page_title: title || document.title,
    page_location: window.location.href,
    page_path: url,
  });

  console.log('📄 Page view tracked:', url);
};

// Track custom events
export const trackEvent = (
  eventName: string,
  parameters: Record<string, any> = {}
) => {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', eventName, {
    event_category: parameters.category || 'engagement',
    event_label: parameters.label,
    value: parameters.value,
    ...parameters,
  });

  console.log('🎯 Event tracked:', eventName, parameters);
};

// SEO-specific event tracking
export const trackSEOEvents = {
  // Track ROI calculator usage
  roiCalculatorStart: () => {
    trackEvent('roi_calculator_start', {
      category: 'calculator',
      label: 'ROI Calculator Started',
    });
  },

  roiCalculatorComplete: (savings: number) => {
    trackEvent('roi_calculator_complete', {
      category: 'calculator',
      label: 'ROI Calculator Completed',
      value: savings,
    });
  },

  // Track demo requests
  demoRequest: (source?: string) => {
    trackEvent('demo_request', {
      category: 'lead_generation',
      label: 'Demo Requested',
      source: source || 'unknown',
    });
  },

  // Track contact form submissions
  contactFormSubmit: (formType: string) => {
    trackEvent('contact_form_submit', {
      category: 'lead_generation',
      label: `Contact Form - ${formType}`,
    });
  },

  // Track blog engagement
  blogRead: (articleTitle: string, readTime: number) => {
    trackEvent('blog_article_read', {
      category: 'content',
      label: articleTitle,
      value: readTime,
    });
  },

  // Track video interactions
  videoPlay: (videoTitle: string) => {
    trackEvent('video_play', {
      category: 'content',
      label: videoTitle,
    });
  },

  videoComplete: (videoTitle: string, duration: number) => {
    trackEvent('video_complete', {
      category: 'content',
      label: videoTitle,
      value: duration,
    });
  },

  // Track download events
  downloadStart: (fileName: string, fileType: string) => {
    trackEvent('file_download', {
      category: 'downloads',
      label: fileName,
      file_type: fileType,
    });
  },

  // Track search behavior
  siteSearch: (searchTerm: string, resultsCount: number) => {
    trackEvent('search', {
      search_term: searchTerm,
      number_of_results: resultsCount,
    });
  },

  // Track navigation patterns
  navigationClick: (linkText: string, destination: string) => {
    trackEvent('navigation_click', {
      category: 'navigation',
      label: linkText,
      destination: destination,
    });
  },

  // Track error pages
  errorPage: (errorType: string, url: string) => {
    trackEvent('error_page', {
      category: 'errors',
      label: `${errorType} - ${url}`,
      error_type: errorType,
      page_url: url,
    });
  },
};

// Conversion tracking
export const trackConversion = (conversionType: string, value?: number) => {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', 'conversion', {
    send_to: GA4_MEASUREMENT_ID,
    event_category: 'conversion',
    event_label: conversionType,
    value: value,
  });

  console.log('💰 Conversion tracked:', conversionType, value);
};

// Enhanced e-commerce tracking (if applicable)
export const trackPurchase = (transactionData: {
  transaction_id: string;
  value: number;
  currency: string;
  items: Array<{
    item_id: string;
    item_name: string;
    category: string;
    quantity: number;
    price: number;
  }>;
}) => {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', 'purchase', {
    transaction_id: transactionData.transaction_id,
    value: transactionData.value,
    currency: transactionData.currency,
    items: transactionData.items,
  });
};

// User engagement metrics
export const trackEngagement = {
  timeOnPage: (duration: number, page: string) => {
    trackEvent('time_on_page', {
      category: 'engagement',
      label: page,
      value: duration,
    });
  },

  scrollDepth: (percentage: number, page: string) => {
    trackEvent('scroll_depth', {
      category: 'engagement',
      label: page,
      value: percentage,
    });
  },

  buttonClick: (buttonName: string, location: string) => {
    trackEvent('button_click', {
      category: 'engagement',
      label: buttonName,
      location: location,
    });
  },
};

// Privacy and consent management
export const updateConsentSettings = (settings: {
  analytics_storage: 'granted' | 'denied';
  ad_storage: 'granted' | 'denied';
}) => {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('consent', 'update', settings);
}; 